import React, { createContext, useContext, useReducer, ReactNode } from "react";

export type ConnectionDetails = {
  serverUrl: string;
  roomName: string;
  participantName: string;
  participantToken: string;
};

// Define the shape of our global state
interface GlobalState {
  chatMode: "voice" | "text" | null;
  isMicrophoneEnabled: boolean;
  micPermission: PermissionState;
  showReconnectPrompt: boolean;
  connectionDetails: ConnectionDetails | undefined;
  storeUrl: string | undefined;
  customerId: string | undefined;
}

// Define action types
type Action =
  | { type: "SET_CHAT_MODE"; payload: "voice" | "text" | null }
  | { type: "SET_MIC_PERMISSION"; payload: PermissionState }
  | { type: "SET_SHOW_RECONNECT_PROMPT"; payload: boolean }
  | { type: "SET_CONNECTION_DETAILS"; payload: ConnectionDetails | undefined }
  | { type: "SET_STORE_URL"; payload: string }
  | { type: "SET_CUSTOMER_ID"; payload: string }
  | { type: "SET_MICROPHONE_ENABLED"; payload: boolean };

// Add more action types here as needed

// Initial state
const initialState: GlobalState = {
  chatMode: window.localStorage.getItem("chatMode") as "voice" | "text" | null,
  isMicrophoneEnabled: false,
  micPermission: "prompt",
  showReconnectPrompt: false,
  connectionDetails: undefined,
  storeUrl: undefined,
  customerId: undefined,
};

// Create context
const GlobalStateContext = createContext<
  | {
      state: GlobalState;
      dispatch: React.Dispatch<Action>;
    }
  | undefined
>(undefined);

// Reducer function
function globalStateReducer(state: GlobalState, action: Action): GlobalState {
  switch (action.type) {
    case "SET_CHAT_MODE":
      return {
        ...state,
        chatMode: action.payload,
      };
    case "SET_SHOW_RECONNECT_PROMPT":
      return {
        ...state,
        showReconnectPrompt: action.payload,
      };
    case "SET_CONNECTION_DETAILS":
      return {
        ...state,
        connectionDetails: action.payload,
      };
    case "SET_STORE_URL":
      return {
        ...state,
        storeUrl: action.payload,
      };
    case "SET_CUSTOMER_ID":
      return {
        ...state,
        customerId: action.payload,
      };
    case "SET_MICROPHONE_ENABLED":
      return {
        ...state,
        isMicrophoneEnabled: action.payload,
      };
    case "SET_MIC_PERMISSION":
      return {
        ...state,
        micPermission: action.payload,
      };
    default:
      return state;
  }
}

// Provider component
export function GlobalStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(globalStateReducer, initialState);

  return (
    <GlobalStateContext.Provider value={{ state, dispatch }}>
      {children}
    </GlobalStateContext.Provider>
  );
}

// Custom hook to use the global state
export function useGlobalState() {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    throw new Error("useGlobalState must be used within a GlobalStateProvider");
  }
  return context;
}
